import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  FormHelperText,
  SelectChangeEvent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  Popper,
  ClickAwayListener,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { ContactFormData } from '../../types/contact';
import { searchCustomersByPhone } from '../../services/contactService';
import { Contact } from '../../types/contact';

interface CustomerDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (customerData: ContactFormData) => void;
  onUpdate?: (customerId: string, customerData: ContactFormData) => void; // دالة تحديث العميل
  initialData: Partial<ContactFormData>;
  branchId?: string;
  customerId?: string; // معرف العميل للتحديث
  searchCustomersFromServer?: (searchTerm: string) => Promise<Contact[]>; // دالة البحث من الخادم ترجع النتائج
}

const CustomerDetailsDialog: React.FC<CustomerDetailsDialogProps> = ({
  open,
  onClose,
  onSave,
  onUpdate,
  initialData,
  branchId,
  customerId,
  searchCustomersFromServer
}) => {
  const [customers, setCustomers] = useState<Contact[]>([]); // قائمة العملاء المحملة من الخادم
  const [selectedCustomerFromSearch, setSelectedCustomerFromSearch] = useState<string | null>(null); // معرف العميل المختار من البحث
  const [customerData, setCustomerData] = useState<Partial<ContactFormData>>({
    type: 'customer',
    status: 'active',
    category: 'regular',
    ...initialData
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [phoneAnchorEl, setPhoneAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // تحديث البيانات الأولية فقط عند فتح النافذة (مرة واحدة فقط)
  useEffect(() => {
    if (open) {
      // دمج البيانات الأولية مع القيم الافتراضية بشكل صحيح
      setCustomerData({
        ...initialData,
        type: 'customer',
        status: initialData.status || 'active',
        category: initialData.category || 'regular'
      });
      // إعادة تعيين جميع الحالات عند فتح النافذة
      setSearchResults([]);
      setSearchTerm('');
      setSelectedCustomerFromSearch(null);
      console.log('تم فتح نافذة إضافة عميل مع البيانات الأولية:', initialData);
    } else {
      // عند إغلاق النافذة، نعيد تعيين كل شيء
      setCustomerData({
        type: 'customer',
        status: 'active',
        category: 'regular'
      });
      setSearchResults([]);
      setSearchTerm('');
      setSelectedCustomerFromSearch(null);
    }
  }, [open]); // فقط عند تغيير حالة فتح/إغلاق النافذة، وليس عند تغيير initialData
  
  // معالجة تغيير الحقول
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!name) return;
    
    setCustomerData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // مسح الخطأ عند تعديل الحقل
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // دالة تحديث حقل الهاتف مع البحث - نفس طريقة الحقل الخارجي
  const handlePhoneChangeWithSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    console.log(`تم إدخال رقم هاتف: ${phone}`);
    
    // تحديث قيمة رقم الهاتف أولاً (مثل الحقل الخارجي)
    setCustomerData(prev => ({
      ...prev,
      phone: phone
    }));
    
    // مسح الخطأ إذا وجد
    if (errors.phone) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.phone;
        return newErrors;
      });
    }
    
    setSearchTerm(phone);
    
    // إلغاء التوقيت السابق إذا كان موجوداً
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      console.log('تم إلغاء توقيت البحث السابق');
    }
    
    // تأخير البحث لتجنب العمليات المتكررة أثناء الكتابة
    const timeout = setTimeout(async () => {
      if (phone.trim().length >= 2) {
        try {
          console.log(`بدء البحث عن العملاء برقم الهاتف من الخادم: ${phone}`);
          
          // البحث من الخادم إذا كانت دالة البحث متوفرة
          if (searchCustomersFromServer) {
            const results = await searchCustomersFromServer(phone);
            console.log('تم البحث من الخادم بنجاح');
            console.log(`تم العثور على ${results.length} عميل مطابق لرقم الهاتف: ${phone}`);
            setSearchResults(results);
            setSearching(false);
            
            // فتح قائمة النتائج تلقائياً إذا تم العثور على عملاء
            if (results.length > 0) {
              // استخدام حقل الهاتف كـ anchor للقائمة
              const phoneInput = document.querySelector('input[name="phone"]') as HTMLElement;
              if (phoneInput) {
                setPhoneAnchorEl(phoneInput);
                console.log('تم فتح قائمة نتائج البحث تلقائياً');
              }
            }
          } else {
            // إذا لم تكن دالة البحث متوفرة، لا نعرض نتائج
            console.log('دالة البحث من الخادم غير متوفرة');
            setSearchResults([]);
            setSearching(false);
          }
        } catch (error) {
          console.error('خطأ في البحث عن العملاء:', error);
          setSearchResults([]);
          setSearching(false);
        }
      } else {
        console.log('الرقم أقل من حرفين، تم مسح نتائج البحث');
        setSearchResults([]);
        setPhoneAnchorEl(null);
      }
    }, 300); // تقليل وقت التأخير لتجربة أسرع
    
    console.log('تم تعيين توقيت جديد للبحث');
    setSearchTimeout(timeout);
  };

  // تحديث العملاء عند تغيير البحث من الخادم
  useEffect(() => {
    if (searchCustomersFromServer) {
      // يمكن إضافة منطق إضافي هنا إذا لزم الأمر
    }
  }, [searchCustomersFromServer]);

  const handlePhoneSearchOld = (value: string) => {
    setCustomerData(prev => ({ ...prev, phone: value }));
    
    // إلغاء التوقيت السابق إذا كان موجوداً
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // تأخير البحث أثناء الكتابة - النسخة القديمة للمرجع
    if (value.trim().length >= 2) {
      setSearching(true);
      
      const timeout = setTimeout(() => {
        // البحث المحلي في قائمة العملاء - هذا لن يعمل الآن
        const cleanedPhone = value.trim().replace(/[\s-+()]/g, '');
        
        const results: Contact[] = []; // قائمة فارغة لأن allCustomers لم تعد متوفرة
        
        console.log(`تم العثور على ${results.length} عميل مطابق لرقم الهاتف: ${value}`);
        setSearchResults(results);
        setSearching(false);
        
        // فتح نافذة البحث فقط إذا كانت هناك نتائج
        if (results.length > 0) {
          // لا نحتاج لفتح النافذة تلقائياً، سيتم فتحها عند النقر على الشريحة
        } else {
          setPhoneAnchorEl(null);
        }
      }, 300);
      
      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
      setPhoneAnchorEl(null);
      setSearching(false);
    }
  };
  
  // اختيار عميل من نتائج البحث
  const handleSelectSearchResult = (customer: Contact) => {
    console.log('تم اختيار عميل من نتائج البحث:', customer);
    
    // استبدال جميع البيانات ببيانات العميل المختار (بدون دمج مع البيانات القديمة)
    const newCustomerData: Partial<ContactFormData> = {
      type: 'customer',
      status: customer.status || 'active',
      category: customer.category || 'regular',
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      taxNumber: customer.taxNumber || '',
      // نسخ بيانات العنوان بشكل كامل
      address: customer.address ? {
        street: customer.address.street || '',
        city: customer.address.city || '',
        state: customer.address.state || '',
        country: customer.address.country || '',
        postalCode: customer.address.postalCode || ''
      } : undefined,
      // تحويل الملاحظات إلى نص إذا كانت موجودة
      notes: Array.isArray(customer.notes)
        ? customer.notes.map((note: any) => note.content).join('\n')
        : customer.notes || '',
      // الاحتفاظ بـ phoneDigitsLimit من البيانات الأولية
      phoneDigitsLimit: customerData.phoneDigitsLimit || initialData.phoneDigitsLimit
    };
    
    setCustomerData(newCustomerData);
    
    // حفظ معرف العميل المختار من البحث
    setSelectedCustomerFromSearch(customer._id);
    
    console.log('تم تحديث بيانات العميل في النافذة بنجاح:', newCustomerData);
    console.log('تم تعيين معرف العميل المختار:', customer._id);
    
    setPhoneAnchorEl(null);
    setSearchResults([]);
  };
  
  // إغلاق نافذة نتائج البحث
  const handleCloseSearchResults = () => {
    setPhoneAnchorEl(null);
  };

  // إعادة تعيين الحالات عند إغلاق النافذة
  const handleClose = () => {
    onClose();
  };
  
  // معالجة تغيير القائمة المنسدلة
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (!name) return;
    
    setCustomerData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // معالجة تغيير حقول العنوان
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setCustomerData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value
      }
    }));
  };
  
  // التحقق من صحة البيانات
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // التحقق من وجود الاسم
    if (!customerData.name || customerData.name.trim() === '') {
      newErrors.name = 'اسم العميل مطلوب';
    }
    
    // التحقق من صحة رقم الهاتف بناءً على العدد المحدد (فقط عند إضافة عميل جديد)
    const phoneDigitsLimit = customerData.phoneDigitsLimit || initialData.phoneDigitsLimit || 10;
    
    // التحقق من وجود رقم الهاتف أولاً (فقط عند إضافة عميل جديد)
    if (!customerData.phone || customerData.phone.trim() === '') {
      newErrors.phone = 'رقم الهاتف مطلوب';
    } else {
      // التحقق من عدد الأرقام
      const cleanedPhone = customerData.phone.replace(/[\s-+()]/g, '');
      const phoneDigits = cleanedPhone.replace(/\D/g, ''); // إزالة جميع الأحرف غير الرقمية
      
      if (phoneDigits.length !== phoneDigitsLimit) {
        newErrors.phone = `رقم الهاتف يجب أن يحتوي على ${phoneDigitsLimit} رقم بالضبط. العدد الحالي: ${phoneDigits.length}`;
      }
    }
    
    // التحقق من صحة البريد الإلكتروني إذا كان موجوداً
    if (customerData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email)) {
      newErrors.email = 'صيغة البريد الإلكتروني غير صحيحة';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // معالجة حفظ البيانات
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    
    // إضافة معرف الفرع إلى بيانات العميل
    const finalData: ContactFormData = {
      ...customerData,
      phoneDigitsLimit: customerData.phoneDigitsLimit || initialData.phoneDigitsLimit || 10,
      type: 'customer',
      status: 'active'
    } as ContactFormData;
    
    // حذف البريد الإلكتروني إذا كان فارغاً لتجنب خطأ التكرار
    if (!finalData.email || finalData.email.trim() === '') {
      delete finalData.email;
    }
    
    // إضافة الفرع الحالي إلى قائمة فروع العميل
    if (branchId) {
      finalData.branches = [...(finalData.branches || []), branchId];
    }
    
    onSave(finalData);
  };
  
  // معالجة تحديث البيانات
  const handleUpdate = () => {
    // التحقق من صحة البيانات (باستثناء رقم الهاتف)
    const newErrors: Record<string, string> = {};
    
    // التحقق من وجود الاسم
    if (!customerData.name || customerData.name.trim() === '') {
      newErrors.name = 'اسم العميل مطلوب';
    }
    
    // التحقق من صحة البريد الإلكتروني إذا كان موجوداً
    if (customerData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email)) {
      newErrors.email = 'صيغة البريد الإلكتروني غير صحيحة';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    // استخدام معرف العميل من البحث إذا كان موجوداً، وإلا استخدام customerId
    const targetCustomerId = selectedCustomerFromSearch || customerId;
    
    if (!targetCustomerId || !onUpdate) {
      console.error('لا يمكن التحديث: معرف العميل أو دالة التحديث غير متوفرة');
      return;
    }
    
    // إعداد بيانات التحديث (بدون رقم الهاتف)
    const finalData: ContactFormData = {
      ...customerData,
      phoneDigitsLimit: customerData.phoneDigitsLimit || initialData.phoneDigitsLimit || 10,
      type: 'customer',
      status: customerData.status || 'active'
    } as ContactFormData;
    
    // حذف رقم الهاتف من بيانات التحديث لمنع تحديثه
    delete finalData.phone;
    
    // إضافة الفرع الحالي إلى قائمة فروع العميل إذا لم يكن موجوداً
    if (branchId) {
      finalData.branches = [...(finalData.branches || []), branchId];
    }
    
    onUpdate(targetCustomerId, finalData);
  };
  
  // معرفة ما إذا كان يجب عرض نافذة البحث
  const open_search_results = Boolean(phoneAnchorEl);
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ bgcolor: '#f5f5f5', p: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          إضافة عميل جديد
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              المعلومات الأساسية
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              label="اسم العميل"
              fullWidth
              value={customerData.name || ''}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="phone"
              label="رقم الهاتف (للبحث)"
              fullWidth
              value={customerData.phone || ''}
              onChange={handlePhoneChangeWithSearch}
              error={!!errors.phone}
              helperText={errors.phone}
              placeholder="أدخل رقم الهاتف للبحث عن العملاء"
              disabled={(customerId || selectedCustomerFromSearch) ? true : false}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="primary" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searching ? (
                      <CircularProgress size={20} color="primary" />
                    ) : searchResults.length > 0 ? (
                      <Chip
                        label={`${searchResults.length} عميل`}
                        size="small"
                        color="primary"
                        onClick={(e) => setPhoneAnchorEl(e.currentTarget)}
                      />
                    ) : (
                      <SearchIcon color="action" fontSize="small" />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: searchResults.length > 0 ? '#e3f2fd' : '#f5f9ff'
                }
              }}
            />
            {/* نافذة نتائج البحث */}
            <Popper
              open={open_search_results}
              anchorEl={phoneAnchorEl}
              placement="bottom-start"
              style={{ zIndex: 9999, width: phoneAnchorEl?.offsetWidth || 300, minWidth: 300 }}
              modifiers={[
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: {
                    boundary: document.body
                  }
                }
              ]}
            >
              <ClickAwayListener onClickAway={handleCloseSearchResults}>
                <Paper elevation={4} sx={{
                  mt: 1,
                  maxHeight: 300,
                  minWidth: 300,
                  width: '100%',
                  overflow: 'auto',
                  border: '1px solid #bbdefb'
                }}>
                  <List dense>
                    <ListItem sx={{ bgcolor: '#e3f2fd' }}>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" fontWeight="bold">
                              العملاء المطابقين لـ "{searchTerm}"
                            </Typography>
                            <IconButton size="small" onClick={handleCloseSearchResults}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider />
                    {searchResults.map((customer) => (
                      <ListItem
                        key={customer._id}
                        button
                        onClick={() => handleSelectSearchResult(customer)}
                        sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={customer.name}
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {customer.phone}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              label="البريد الإلكتروني"
              fullWidth
              value={customerData.email || ''}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="taxNumber"
              label="الرقم الضريبي"
              fullWidth
              value={customerData.taxNumber || ''}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>فئة العميل</InputLabel>
              <Select
                name="category"
                value={customerData.category || 'regular'}
                onChange={handleSelectChange}
                label="فئة العميل"
              >
                <MenuItem value="regular">عادي</MenuItem>
                <MenuItem value="premium">مميز</MenuItem>
                <MenuItem value="vip">VIP</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              العنوان
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="street"
              label="الشارع"
              fullWidth
              value={customerData.address?.street || ''}
              onChange={handleAddressChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="city"
              label="المدينة"
              fullWidth
              value={customerData.address?.city || ''}
              onChange={handleAddressChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="state"
              label="المنطقة/المحافظة"
              fullWidth
              value={customerData.address?.state || ''}
              onChange={handleAddressChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="country"
              label="الدولة"
              fullWidth
              value={customerData.address?.country || ''}
              onChange={handleAddressChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="postalCode"
              label="الرمز البريدي"
              fullWidth
              value={customerData.address?.postalCode || ''}
              onChange={handleAddressChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              ملاحظات
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="notes"
              label="ملاحظات"
              fullWidth
              multiline
              rows={3}
              value={customerData.notes || ''}
              onChange={handleInputChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Button onClick={handleClose} variant="outlined" color="primary">
          إلغاء
        </Button>
        {selectedCustomerFromSearch && !customerId && (
          <Button
            onClick={() => {
              setSelectedCustomerFromSearch(null);
              setCustomerData({
                type: 'customer',
                status: 'active',
                category: 'regular',
                name: '',
                phone: '',
                email: '',
                taxNumber: '',
                address: undefined,
                notes: '',
                phoneDigitsLimit: initialData.phoneDigitsLimit || 10
              });
            }}
            variant="outlined"
            color="warning"
          >
            إلغاء الاختيار وإضافة عميل جديد
          </Button>
        )}
        {(customerId || selectedCustomerFromSearch) && onUpdate ? (
          <Button
            onClick={handleUpdate}
            variant="contained"
            color="success"
          >
            تحديث معلومات العميل
          </Button>
        ) : (
          <Button onClick={handleSubmit} variant="contained" color="primary">
            حفظ العميل
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDetailsDialog;